import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { userInfoStore } from '@pascal/pascal-core/shared/core/store';
import { DefaultQueryParams, parseNumberByPrecision, parseQuery } from '@pascal/pascal-core';
import { getBonusIslandImageSource, marketTypeToName } from '../../../helpers';
import RtlLanguages from '../../../enums/rtl-languages.enum';
import { bonusIslandStore } from '../../../store/bonusIslandStore';
import { AllMarketTypes, TOURNAMENT_STATUS } from '../../../enums';

export /**
 * @param setIsOpen {function} - set bonus island map open state
 * @param tournamentSelections {Array} - list of banner selections
 * @param tournamentRanges {Array} - list of banner ranges
 * @param tournamentEndDate {Date} - bonus island end date
 * @param precision {number} - precision for numbers
 * @param currencyCode {string} - currency code of user or partner
 * @param isShowLoader {boolean} - show bonus island loader on range GET request time
 * @param t {function} - translation function
 */
const BonusIslandMap = observer(
  ({
    tournamentSelections = [],
    tournamentRanges = [],
    tournamentStartDate,
    tournamentEndDate,
    tournamentStatus,
    setIsOpen,
    precision = 2,
    currencyCode,
    currency,
    loading = false,
    innerComp: InnerComp,
    innerCompModal: InnerCompModal,
    getTournamentDataAction,
    t,
  }) => {
    const { liveUpdateData } = bonusIslandStore;
    const [selectedRangeIndex, setSelectedRangeIndex] = useState(0);
    const [bottomPartSelectionsInfo, setBottomPartSelectionsInfo] = useState([]);
    const [tournamentTotalPrizeFund, setTournamentTotalPrizeFund] = useState(0);
    const [mappedTournamentSelections, setMappedTournamentSelections] = useState([]);
    const [rangeCollectedProgress, setRangeCollectedProgress] = useState(0);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [displayNameTranslations, setDisplayNameTranslations] = useState([]);
    const { [DefaultQueryParams.Culture]: culture } = parseQuery(window.location.search);

    const setSelectionsWidth = (tournamentSelections) => {
      const rangeSelectionCellWidth = 100 / tournamentSelections.length;
      let rangeSelectionWidth = 0;
      let bottomPartSelections = [];
      const mappedTournamentSelectionsData = tournamentSelections.map((selection) => {
        const selectionCellWidth = 100 / selection.stages.length;
        const item = { ...selection };
        let prevStage;
        let selectionWidth = 0;
        if (selection?.userTournamentSelections?.[0]?.isCollected) {
          bottomPartSelections = [{ isCollected: true }, ...bottomPartSelections];
          rangeSelectionWidth += rangeSelectionCellWidth;
        } else {
          bottomPartSelections = [...bottomPartSelections, { isCollected: false }];
        }

        item.stages = selection.stages.map((stage, index) => {
          const stageData = { ...stage };
          if (
            (!stage.userTournamentStages ||
              !stage.userTournamentStages.length ||
              !stage.userTournamentStages[0].isCollected) &&
            !item.nextNotCollectedStagePointsCount
          ) {
            item.nextNotCollectedStagePointsCount = stage.count;
          }

          if (index) {
            prevStage = selection.stages[index - 1];
          }

          if (
            stage.userTournamentStages &&
            stage.userTournamentStages.length &&
            stage.userTournamentStages[0].isCollected
          ) {
            selectionWidth += selectionCellWidth;
          } else if (
            stage.userTournamentStages &&
            stage.userTournamentStages.length &&
            !stage.userTournamentStages[0].isCollected &&
            ((prevStage && prevStage.userTournamentStages[0].isCollected) || !index)
          ) {
            const prevStageCount = index ? prevStage.count : 0;
            const currentStageRestCount =
              selection.userTournamentSelections[0].currentCount - prevStageCount;
            const currentStagePointInterval = stage.count - prevStageCount;
            selectionWidth +=
              selectionCellWidth * (currentStageRestCount / currentStagePointInterval);
          }
          return stageData;
        });
        item.userCollectedProgressWidth = selectionWidth;
        return item;
      });
      const rangeCollectedProgressData = Math.round(100 - rangeSelectionWidth);
      setMappedTournamentSelections(mappedTournamentSelectionsData);
      setBottomPartSelectionsInfo(bottomPartSelections);
      setRangeCollectedProgress(rangeCollectedProgressData);
    };

    useEffect(() => {
      const { selectionId, isCollected, userId } = liveUpdateData;
      if (+userInfoStore?.userId === +userId) {
        const updatedTournamentSelections = mappedTournamentSelections.map((selection) => {
          if (selection.selectionId === selectionId) {
            const selectionCopy = { ...selection };
            selectionCopy.userTournamentSelections = [
              {
                currentCount: selectionCopy.userTournamentSelections.length
                  ? selectionCopy.userTournamentSelections[0].currentCount + 1
                  : 1,
                isCollected,
              },
            ];

            const firstNotCollectedStageIndex = selection.stages.findIndex(
              (stage) => !stage.userTournamentStages[0]?.isCollected
            );
            selectionCopy.stages = selection.stages.map((stage, i) => {
              if (i === firstNotCollectedStageIndex) {
                const currentCount = stage.userTournamentStages.length
                  ? stage.userTournamentStages[0].currentCount + 1
                  : 1;
                return {
                  ...stage,
                  userTournamentStages: [
                    {
                      currentCount,
                      isCollected:
                        currentCount === selection.stages[firstNotCollectedStageIndex].count,
                    },
                  ],
                };
              }
              return stage;
            });
            selectionCopy.nextNotCollectedStagePointsCount = null;

            return selectionCopy;
          }
          return selection;
        });
        setSelectionsWidth(updatedTournamentSelections);
      }
    }, [liveUpdateData]);

    useEffect(() => {
      const selectedRangeId = tournamentRanges?.[selectedRangeIndex]?.rangeId;
      getTournamentDataAction(selectedRangeId);
    }, [selectedRangeIndex]);

    useEffect(() => {
      if (tournamentRanges.length) {
        const allTournamentTotalPrizeFund = tournamentRanges.reduce((acc, curr) => {
          return (
            acc +
            parseNumberByPrecision(1, curr.overalPrizeFund, precision) +
            parseNumberByPrecision(1, curr.totalPrizeFund, precision)
          );
        }, 0);
        setTournamentTotalPrizeFund(allTournamentTotalPrizeFund);
      }
    }, [tournamentRanges]);

    const translateDisplayNames = (displayName) => {
      return displayName
        .split(',')
        .map((betType) => marketTypeToName(AllMarketTypes[betType], t))
        .join(', ');
    };

    useEffect(() => {
      if (tournamentSelections.length) {
        setSelectionsWidth(tournamentSelections);
        setDisplayNameTranslations(
          tournamentSelections.map((selection) => translateDisplayNames(selection.displayName))
        );
      }
    }, [tournamentSelections]);

    const isRtl = useMemo(() => {
      return RtlLanguages.includes(culture);
    }, [culture]);

    const isFinished = tournamentStatus === TOURNAMENT_STATUS.FINISHED;
    const tournamentStatusText = isFinished ? 'FINISHED' : 'END_DATE';

    return (
      <>
        {isInfoModalOpen && (
          <InnerCompModal
            currencyCode={currencyCode}
            currency={currency}
            endDate={tournamentEndDate}
            startDate={tournamentStartDate}
            maxBonus={tournamentTotalPrizeFund}
            tournamentRanges={tournamentRanges}
            displayNames={displayNameTranslations}
            onClose={() => setIsInfoModalOpen(false)}
            stagesCount={tournamentSelections[0]?.stages.length}
            minAmount={tournamentRanges[selectedRangeIndex].minAmount}
            minBonus={tournamentSelections[0]?.stages?.[0]?.stagePrizes?.[0]?.prizeFund}
          />
        )}
        <InnerComp
          t={t}
          isRtl={isRtl}
          loading={loading}
          setIsOpen={setIsOpen}
          isFinished={isFinished}
          currencyCode={currencyCode}
          currency={currency}
          tournamentRanges={tournamentRanges}
          tournamentEndDate={tournamentEndDate}
          selectedRangeIndex={selectedRangeIndex}
          imageSrc={getBonusIslandImageSource(culture)}
          tournamentStatusText={tournamentStatusText}
          setSelectedRangeIndex={setSelectedRangeIndex}
          translateDisplayNames={translateDisplayNames}
          bottomPartSelections={bottomPartSelectionsInfo}
          rangeCollectedProgress={rangeCollectedProgress}
          tournamentSelections={mappedTournamentSelections}
          tournamentTotalPrizeFund={tournamentTotalPrizeFund}
          openMainBonusIslandInfoPopup={() => setIsInfoModalOpen(true)}
        />
      </>
    );
  }
);
